import { uiText } from '../i18n/uiText';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { auditorPoolApi, getApiErrorMessage } from '../api';
import { CalendarDays } from 'lucide-react';
import PageIntro from '../components/ui/PageIntro';

function monthBounds(cursor) {
  const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  return { start, end };
}

function isoDate(date) {
  return date.toISOString().slice(0, 10);
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getDaySummary(selectedProfiles, day, eventMap) {
  let free = 0;
  let pending = 0;
  let accepted = 0;

  selectedProfiles.forEach((profile) => {
    const bucket = eventMap.get(`${isoDate(day)}:${profile.user_id}`) || [];
    if (!bucket.length) {
      free += 1;
      return;
    }
    if (bucket.some((item) => item.assignment_status === 'accepted')) {
      accepted += 1;
    } else {
      pending += 1;
    }
  });

  return { free, pending, accepted, busy: pending + accepted, total: selectedProfiles.length };
}

function getLoadTone(summary) {
  if (!summary.total || summary.free === summary.total) {
    return {
      tone: 'free',
      label: uiText("Все свободны"),
      pillBg: '#dcfce7',
      pillColor: '#166534',
      cardBg: '#f0fdf4',
      border: '#86efac',
      accent: '#16a34a',
    };
  }
  if (summary.free === 0) {
    return {
      tone: 'busy',
      label: uiText("Все заняты"),
      pillBg: '#fee2e2',
      pillColor: '#991b1b',
      cardBg: '#fff7f7',
      border: '#fca5a5',
      accent: '#dc2626',
    };
  }
  return {
    tone: 'partial',
    label: uiText("Частичная занятость"),
    pillBg: '#fef3c7',
    pillColor: '#92400e',
    cardBg: '#fffdf6',
    border: '#fcd34d',
    accent: '#d97706',
  };
}

function getEntryTone(bucket) {
  if (!bucket.length) {
    return {
      tone: 'free',
      label: uiText("Свободен"),
      bg: '#f0fdf4',
      border: '#86efac',
      color: '#166534',
      dot: '#16a34a',
    };
  }
  if (bucket.some((item) => item.assignment_status === 'accepted')) {
    return {
      tone: 'accepted',
      label: uiText("Подтвержден"),
      bg: '#eff6ff',
      border: '#93c5fd',
      color: '#1d4ed8',
      dot: '#2563eb',
    };
  }
  return {
    tone: 'pending',
    label: uiText("Ожидает ответа"),
    bg: '#fff7ed',
    border: '#fdba74',
    color: '#c2410c',
    dot: '#f97316',
  };
}

function MiniStat({ label, value, color, hint }) {
  return (
    <div
      className="auditor-schedule-stat"
      style={{
        background: '#fff',
        border: '1px solid var(--border-color)',
        borderRadius: 18,
        padding: '16px 18px',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.04)',
      }}
    >
      <div className="auditor-schedule-stat-label" style={{ fontSize: 12, color:'var(--premium-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ marginTop: 8, fontSize: 30, fontWeight: 800, color }}>{value}</div>
      {hint ? <div className="auditor-schedule-stat-hint" style={{ marginTop: 6, fontSize: 12, color:'var(--premium-text-secondary)' }}>{hint}</div> : null}
    </div>
  );
}

export default function AuditorSchedulePage() {
  const [cursor, setCursor] = useState(new Date());
  const [profiles, setProfiles] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [error, setError] = useState('');

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const res = await auditorPoolApi.list();
      const items = Array.isArray(res.data) ? res.data : [];
      setProfiles(items);
      setSelectedIds((prev) => (prev.length ? prev : items.slice(0, Math.min(items.length, 4)).map((item) => item.user_id)));
      setError('');
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfiles();
  }, []);

  useEffect(() => {
    if (!profiles.length) return;
    const fetchCalendar = async () => {
      setLoadingCalendar(true);
      try {
        const { start, end } = monthBounds(cursor);
        const res = await auditorPoolApi.schedule({
          start: isoDate(start),
          end: isoDate(end),
          auditor_user_ids: selectedIds,
        });
        setEvents(Array.isArray(res.data?.events) ? res.data.events : []);
        setError('');
      } catch (e) {
        setError(getApiErrorMessage(e));
      } finally {
        setLoadingCalendar(false);
      }
    };
    fetchCalendar();
  }, [cursor, selectedIds, profiles.length]);

  const filteredProfiles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return profiles;
    return profiles.filter((profile) => {
      const specs = Array.isArray(profile.specializations) ? profile.specializations : [];
      return [profile.full_name, profile.job_title, specs.join(' ')].join(' ').toLowerCase().includes(q);
    });
  }, [profiles, search]);

  const selectedProfiles = useMemo(() => {
    const indexMap = new Map(selectedIds.map((id, index) => [id, index]));
    return profiles
      .filter((profile) => selectedIds.includes(profile.user_id))
      .sort((a, b) => (indexMap.get(a.user_id) ?? 0) - (indexMap.get(b.user_id) ?? 0));
  }, [profiles, selectedIds]);

  const eventMap = useMemo(() => {
    const map = new Map();
    events.forEach((event) => {
      if (!event.scheduled_day) return;
      const key = `${event.scheduled_day}:${event.auditor_user_id}`;
      const bucket = map.get(key) || [];
      bucket.push(event);
      map.set(key, bucket);
    });
    return map;
  }, [events]);

  const days = useMemo(() => {
    const { start } = monthBounds(cursor);
    const firstWeekDay = (start.getDay() + 6) % 7;
    const gridStart = new Date(start);
    gridStart.setDate(start.getDate() - firstWeekDay);
    return Array.from({ length: 42 }, (_, index) => {
      const day = new Date(gridStart);
      day.setDate(gridStart.getDate() + index);
      return day;
    });
  }, [cursor]);

  const today = new Date();

  const monthStats = useMemo(() => {
    let accepted = 0;
    let pending = 0;
    const busyDays = new Set();

    days.forEach((day) => {
      const summary = getDaySummary(selectedProfiles, day, eventMap);
      accepted += summary.accepted;
      pending += summary.pending;
      if (summary.busy > 0 && day.getMonth() === cursor.getMonth()) {
        busyDays.add(isoDate(day));
      }
    });

    const todaySummary = getDaySummary(selectedProfiles, today, eventMap);

    return {
      selected: selectedProfiles.length,
      accepted,
      pending,
      busyDays: busyDays.size,
      todaySummary,
    };
  }, [days, selectedProfiles, eventMap, cursor, today]);

  const toggleAuditor = (userId) => {
    setSelectedIds((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
  };

  const monthLabel = cursor.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });

  return (
    <div className="workspace-page">
      <PageIntro
        icon={CalendarDays}
        title={uiText("График работы аудиторов")}
        description={uiText("Выберите аудиторов и сравните их доступность, ожидающие приглашения и подтверждённые аудиты в едином календаре.")}
      />

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        <MiniStat label={uiText("Выбрано аудиторов")} value={monthStats.selected} color="#0f172a" hint={uiText("Попадают в календарь ниже")} />
        <MiniStat label={uiText("Свободны сегодня")} value={monthStats.todaySummary.free} color="#16a34a" hint={uiText("На выбранную дату")} />
        <MiniStat label={uiText("Ожидают ответа")} value={monthStats.pending} color="#ea580c" hint={uiText("По текущему месяцу")} />
        <MiniStat label={uiText("Подтверждённых слотов")} value={monthStats.accepted} color="#2563eb" hint={uiText("По текущему месяцу")} />
        <MiniStat label={uiText("Дней с занятостью")} value={monthStats.busyDays} color="#7c3aed" hint={uiText("В пределах месяца")} />
      </div>

      <div className="card" style={{ marginBottom: 16, overflow: 'hidden' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
          <div className="card-title">{uiText("Фильтр по аудиторам")}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setSelectedIds(filteredProfiles.map((item) => item.user_id))}>{uiText("Выбрать всех в фильтре")}</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setSelectedIds([])}>{uiText("Снять выбор")}</button>
          </div>
        </div>
        <div className="card-body">
          <div className="auditor-schedule-guide-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 360px) 1fr', gap: 16, alignItems: 'start' }}>
            <div>
              <div className="search-bar" style={{ marginBottom: 12 }}>
                <input placeholder={uiText("Поиск по имени, роли или специализации")} value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {[
                  { label: uiText("Свободен"), bg: '#dcfce7', color: '#166534' },
                  { label: uiText("Ожидает ответа"), bg: '#ffedd5', color: '#c2410c' },
                  { label: uiText("Подтверждён"), bg: '#dbeafe', color: '#1d4ed8' },
                ].map((item) => (
                  <span key={item.label} className="auditor-schedule-legend-pill" style={{ background: item.bg, color: item.color, padding: '8px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>
                    {item.label}
                  </span>
                ))}
              </div>
            </div>
            <div
              className="auditor-schedule-guide"
              style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                border: '1px solid var(--border-color)',
                borderRadius: 18,
                padding: 16,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 8 }}>{uiText("Как читать календарь")}</div>
              <div className="auditor-schedule-guide-copy" style={{ display: 'grid', gap: 8, color:'var(--premium-text-secondary)', fontSize: 14 }}>
                <div>{uiText("Зелёный фон дня — все выбранные аудиторы свободны.")}</div>
                <div>{uiText("Жёлтый фон — часть команды занята или ждёт подтверждения.")}</div>
                <div>{uiText("Красный фон — на день свободных среди выбранных нет.")}</div>
                <div>{uiText("Внутри карточки дня каждая строка показывает статус конкретного аудитора.")}</div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="loading-center" style={{ minHeight: 180 }}><div className="spinner" /></div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginTop: 16 }}>
              {filteredProfiles.map((profile) => {
                const checked = selectedIds.includes(profile.user_id);
                const specs = Array.isArray(profile.specializations) ? profile.specializations : [];
                return (
                  <label
                    key={profile.user_id}
                    className={`card auditor-schedule-profile${checked ? ' is-selected' : ''}`}
                    style={{
                      padding: 16,
                      cursor: 'pointer',
                      border: checked ? '1px solid #2563eb' : '1px solid var(--border-color)',
                      background: checked ? 'linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)' : '#fff',
                      boxShadow: checked ? '0 12px 30px rgba(37, 99, 235, 0.12)' : '0 8px 24px rgba(15, 23, 42, 0.04)',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <input type="checkbox" checked={checked} onChange={() => toggleAuditor(profile.user_id)} style={{ marginTop: 4 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'start' }}>
                          <div>
                            <div style={{ fontWeight: 800, fontSize: 16 }}>{profile.full_name}</div>
                            <div className="auditor-schedule-profile-subtitle" style={{ color:'var(--premium-text-secondary)', fontSize: 13, marginTop: 2 }}>{profile.job_title || uiText("Аудитор")}</div>
                          </div>
                          <span className="auditor-schedule-selection-pill" style={{ background: checked ? '#dbeafe' : '#f8fafc', color: checked ? '#1d4ed8' : '#64748b', padding: '6px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>
                            {checked ? uiText("В календаре") : uiText("Не выбран")}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                          <span className="status-badge status-info">{uiText("Активных: ")}{profile.workload?.active || 0}</span>
                          <span className="status-badge status-neutral">{uiText("Ожидают: ")}{profile.workload?.pending || 0}</span>
                          <span className="status-badge status-success">{uiText("Подтверждено: ")}{profile.workload?.accepted || 0}</span>
                        </div>
                        {specs.length ? (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
                            {specs.slice(0, 4).map((spec) => (
                              <span key={spec} className="auditor-schedule-specialization" style={{ background: '#f8fafc', color:'var(--premium-text-secondary)', padding: '6px 10px', borderRadius: 999, fontSize: 12 }}>
                                {spec}
                              </span>
                            ))}
                            {specs.length > 4 ? <span className="auditor-schedule-muted" style={{ color:'var(--premium-text-secondary)', fontSize: 12, alignSelf: 'center' }}>+{specs.length - 4}</span> : null}
                          </div>
                        ) : null}
                        {profile.available_from ? <div className="auditor-schedule-muted" style={{ color:'var(--premium-text-secondary)', fontSize: 12, marginTop: 10 }}>{uiText("Свободен с ")}{new Date(profile.available_from).toLocaleDateString('ru-RU')}</div> : null}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
          <div>
            <div className="card-title" style={{ textTransform: 'capitalize' }}>{monthLabel}</div>
            <div className="auditor-schedule-muted" style={{ color:'var(--premium-text-secondary)', fontSize: 13 }}>{uiText("Выбрано аудиторов: ")}{selectedProfiles.length}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>{uiText("← Предыдущий")}</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCursor(new Date())}>{uiText("Текущий месяц")}</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>{uiText("Следующий →")}</button>
          </div>
        </div>
        <div className="card-body">
          {loadingCalendar && <div className="auditor-schedule-muted" style={{ marginBottom: 12, color:'var(--premium-text-secondary)' }}>{uiText("Обновляем календарь…")}</div>}
          {!selectedProfiles.length ? (
            <div className="empty-state">
              <p>{uiText("Выберите хотя бы одного аудитора, чтобы увидеть загрузку по дням.")}</p>
            </div>
          ) : (
            <div className="auditor-calendar-scroll" role="region" aria-label={uiText("Календарь занятости аудиторов")} tabIndex={0}>
              <div className="auditor-calendar-weekdays" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 8, marginBottom: 8 }}>
                {[uiText("Пн"), uiText("Вт"), uiText("Ср"), uiText("Чт"), uiText("Пт"), uiText("Сб"), uiText("Вс")].map((day) => (
                  <div key={day} className="auditor-calendar-weekday" style={{ padding: '8px 10px', fontWeight: 700, color:'var(--premium-text-secondary)', textAlign: 'center' }}>{day}</div>
                ))}
              </div>
              <div className="auditor-calendar-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 10 }}>
                {days.map((day) => {
                  const inMonth = day.getMonth() === cursor.getMonth();
                  const summary = getDaySummary(selectedProfiles, day, eventMap);
                  const tone = getLoadTone(summary);
                  const isToday = sameDay(day, today);

                  return (
                    <div
                      key={day.toISOString()}
                      className={`auditor-calendar-day-card tone-${tone.tone}${inMonth ? '' : ' is-outside-month'}${isToday ? ' is-today' : ''}`}
                      style={{
                        minHeight: 210,
                        border: `1px solid ${tone.border}`,
                        borderRadius: 18,
                        padding: 12,
                        background: inMonth ? tone.cardBg : '#f8fafc',
                        opacity: inMonth ? 1 : 0.58,
                        boxShadow: isToday ? '0 0 0 2px rgba(37, 99, 235, 0.18)' : 'none',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 8, marginBottom: 10 }}>
                        <div>
                          <div style={{ fontWeight: 800, fontSize: 18 }}>{day.getDate()}</div>
                          {isToday ? <div className="auditor-calendar-today-label" style={{ color: '#2563eb', fontSize: 12, fontWeight: 700, marginTop: 2 }}>{uiText("Сегодня")}</div> : null}
                        </div>
                        <span
                          className={`auditor-calendar-load-pill tone-${tone.tone}`}
                          style={{
                            background: tone.pillBg,
                            color: tone.pillColor,
                            padding: '7px 10px',
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 800,
                            textAlign: 'right',
                          }}
                        >
                          {tone.label}
                        </span>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6, marginBottom: 10 }}>
                        <div className="auditor-calendar-mini-stat" style={{ background: '#fff', borderRadius: 12, padding: '8px 8px', textAlign: 'center', border: '1px solid rgba(148, 163, 184, 0.16)' }}>
                          <div style={{ fontSize: 11, color:'var(--premium-text-secondary)' }}>{uiText("Свободны")}</div>
                          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--premium-success)' }}>{summary.free}</div>
                        </div>
                        <div className="auditor-calendar-mini-stat" style={{ background: '#fff', borderRadius: 12, padding: '8px 8px', textAlign: 'center', border: '1px solid rgba(148, 163, 184, 0.16)' }}>
                          <div style={{ fontSize: 11, color:'var(--premium-text-secondary)' }}>{uiText("Ожидают")}</div>
                          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--premium-warning)' }}>{summary.pending}</div>
                        </div>
                        <div className="auditor-calendar-mini-stat" style={{ background: '#fff', borderRadius: 12, padding: '8px 8px', textAlign: 'center', border: '1px solid rgba(148, 163, 184, 0.16)' }}>
                          <div style={{ fontSize: 11, color:'var(--premium-text-secondary)' }}>{uiText("Заняты")}</div>
                          <div style={{ fontSize: 16, fontWeight: 800, color: '#2563eb' }}>{summary.accepted}</div>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gap: 8 }}>
                        {selectedProfiles.map((profile) => {
                          const bucket = eventMap.get(`${isoDate(day)}:${profile.user_id}`) || [];
                          const first = bucket[0];
                          const entryTone = getEntryTone(bucket);

                          return (
                            <div
                              key={`${profile.user_id}-${isoDate(day)}`}
                              className={`auditor-calendar-entry tone-${entryTone.tone}`}
                              style={{
                                fontSize: 12,
                                color: entryTone.color,
                                borderRadius: 12,
                                background: entryTone.bg,
                                padding: '9px 10px',
                                border: `1px solid ${entryTone.border}`,
                                borderLeft: `4px solid ${entryTone.dot}`,
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                <strong className="auditor-calendar-entry-name" style={{ color: '#0f172a' }}>{profile.full_name}</strong>
                                <span style={{ color: entryTone.color, fontWeight: 700 }}>{entryTone.label}</span>
                              </div>
                              {first ? (
                                <>
                                  <Link to={`/audits/${first.audit_id}`} style={{ display: 'inline-block', marginTop: 4, fontWeight: 700 }}>
                                    {first.audit_title}
                                  </Link>
                                  <div style={{ marginTop: 4, color: entryTone.color }}>
                                    {bucket.length > 1 ? uiText("Аудитов в этот день: {v0}", {v0: (bucket.length)}) : uiText("Один аудит на дату")}
                                  </div>
                                </>
                              ) : (
                                <div style={{ marginTop: 4, color: entryTone.color }}>{uiText("День свободен для назначения.")}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
